import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Plus, Trash2, Users, User } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const BLANK = { title: "", position: "", kind: "individual", event: "", level: "", date: "", note: "" };
const LEVELS = ["", "Club", "District", "State", "National", "International"];

/** Small honours badge for headers · shows the medal count and the most recent title on hover. */
export function AwardsBadge({ athleteId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!athleteId) return;
    api.get(`/athletes/${athleteId}/awards`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [athleteId]);
  if (!data?.count) return null;
  const top = data.top;
  return (
    <span data-testid="awards-badge" title={top ? `${top.position ? `${top.position} · ` : ""}${top.title}` : ""}
      className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-foreground inline-flex items-center gap-1">
      <Trophy className="w-3 h-3" />
      {data.count}
      {top?.kind === "team" ? <Users className="w-3 h-3" /> : <User className="w-3 h-3" />}
    </span>
  );
}

export default function Awards({ athleteId, canEdit = true }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(BLANK);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!athleteId) return;
    api.get(`/athletes/${athleteId}/awards`).then((r) => setRows(r.data.awards || [])).catch(() => setRows([]));
  }, [athleteId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.title.trim()) return toast.error("What was the award?");
    try {
      await api.post(`/athletes/${athleteId}/awards`, { ...f, title: f.title.trim() });
      toast.success("Award recorded");
      setF(BLANK); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that award"); }
  };
  const del = async (id) => {
    try { await api.delete(`/athletes/${athleteId}/awards/${id}`); load(); }
    catch { toast.error("Could not remove that award"); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div data-testid="awards-panel">
      {canEdit && (
        <div className="mb-4">
          {!open ? (
            <button data-testid="award-add-open" onClick={() => setOpen(true)}
              className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-surface transition-colors flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Record an award
            </button>
          ) : (
            <div className="border border-border rounded-xl p-4 grid sm:grid-cols-2 gap-3" data-testid="award-form">
              <label className="block sm:col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Award</span>
                <input data-testid="award-title" value={f.title} onChange={set("title")} className="inp mt-1"
                  placeholder="National Championships, Player of the Series…" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Position / medal</span>
                <input data-testid="award-position" value={f.position} onChange={set("position")} className="inp mt-1"
                  placeholder="Gold · 1st · Runner-up" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Individual or team</span>
                <select data-testid="award-kind" value={f.kind} onChange={set("kind")} className="inp mt-1">
                  <option value="individual">Individual</option>
                  <option value="team">Team</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Event</span>
                <input data-testid="award-event" value={f.event} onChange={set("event")} className="inp mt-1" placeholder="74 kg freestyle" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Level</span>
                <select data-testid="award-level" value={f.level} onChange={set("level")} className="inp mt-1">
                  {LEVELS.map((l) => <option key={l} value={l}>{l || "-"}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Date</span>
                <input data-testid="award-date" type="date" value={f.date} onChange={set("date")} className="inp mt-1" />
              </label>
              <div className="sm:col-span-2 flex gap-2 justify-end">
                <button data-testid="award-cancel" onClick={() => { setOpen(false); setF(BLANK); }}
                  className="text-sm px-4 py-2 rounded-lg border border-border hover:bg-surface transition-colors">Cancel</button>
                <button data-testid="award-save" onClick={add}
                  className="text-sm px-4 py-2 rounded-lg bg-foreground text-background hover:bg-neutral-800 transition-colors">Save award</button>
              </div>
            </div>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="awards-empty">
          No awards recorded yet. Medals, titles and team honours show up here and as a badge next to the athlete.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="awards-list">
          {rows.map((a) => (
            <div key={a.award_id} className="border border-border rounded-xl p-3 flex items-start gap-3" data-testid={`award-${a.award_id}`}>
              <Trophy className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{a.position ? `${a.position} · ` : ""}{a.title}</div>
                <div className="text-xs text-muted-foreground">
                  {[a.kind === "team" ? "Team" : "Individual", a.event, a.level, a.date && fmtDate(a.date)].filter(Boolean).join(" · ")}
                </div>
              </div>
              {canEdit && (
                <button data-testid={`award-del-${a.award_id}`} onClick={() => del(a.award_id)}
                  className="text-muted-foreground hover:text-traffic-red transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
